import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FiSearch } from 'react-icons/fi';
import * as wasm from 'sched';

export type ValueTargetType = 'Task' | 'Outcome' | 'Problem';

export interface ValueTargetOption {
  type: ValueTargetType;
  id: string;
  title: string;
}

export const TARGET_TYPE_COLORS: Record<ValueTargetType, string> = {
  Task: 'bg-blue-500/20 text-blue-400',
  Outcome: 'bg-green-500/20 text-green-400',
  Problem: 'bg-red-500/20 text-red-400',
};

export const TARGET_BAR_COLORS: Record<string, string> = {
  Task: 'bg-blue-500',
  Outcome: 'bg-green-500',
  Problem: 'bg-red-500',
};

export function labelForValueTarget(target: { type: string; id: string }, database: wasm.WrappedDatabase | null): string {
  if (!database) return target.id;
  if (target.type === 'Task') {
    return database.tasks()[target.id]?.title() ?? target.id;
  }
  if (target.type === 'Outcome') {
    return database.outcomes()[target.id]?.title() ?? target.id;
  }
  if (target.type === 'Problem') {
    return database.problems()[target.id]?.title() ?? target.id;
  }
  return target.id;
}

function useValueTargetSearch(database: wasm.WrappedDatabase | null, query: string): ValueTargetOption[] {
  return useMemo(() => {
    if (!database || query.length === 0) return [];
    const lower = query.toLowerCase();
    const results: ValueTargetOption[] = [];
    const limit = 20;

    for (const [id, task] of Object.entries(database.tasks())) {
      if (results.length >= limit) break;
      const title = task.title();
      if (title.toLowerCase().includes(lower)) {
        results.push({ type: 'Task', id, title });
      }
    }
    for (const [id, outcome] of Object.entries(database.outcomes())) {
      if (results.length >= limit) break;
      const title = outcome.title();
      if (title.toLowerCase().includes(lower)) {
        results.push({ type: 'Outcome', id, title });
      }
    }
    for (const [id, problem] of Object.entries(database.problems())) {
      if (results.length >= limit) break;
      const title = problem.title();
      if (title.toLowerCase().includes(lower)) {
        results.push({ type: 'Problem', id, title });
      }
    }

    return results.sort((a, b) => a.title.localeCompare(b.title));
  }, [database, query]);
}

interface ValueTargetSearchProps {
  database: wasm.WrappedDatabase | null;
  selected: ValueTargetOption | null;
  onSelect: (option: ValueTargetOption | null) => void;
}

const ValueTargetSearch: React.FC<ValueTargetSearchProps> = ({ database, selected, onSelect }) => {
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchResults = useValueTargetSearch(database, query);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (selected) {
    return (
      <div className="flex items-center gap-2 bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-2">
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${TARGET_TYPE_COLORS[selected.type]}`}>
          {selected.type}
        </span>
        <span className="text-sm text-cm-text-primary truncate flex-1">{selected.title}</span>
        <button
          onClick={() => { onSelect(null); setQuery(''); }}
          className="text-cm-text-muted hover:text-cm-text-primary text-xs"
        >
          &times;
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cm-text-muted" />
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setShowResults(true); }}
          onFocus={() => setShowResults(true)}
          className="w-full bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg pl-9 pr-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Search tasks, outcomes, problems..."
        />
      </div>
      {showResults && query.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-cm-bg-surface border border-cm-border-mid rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {searchResults.length === 0 ? (
            <div className="px-3 py-2 text-sm text-cm-text-muted">No matches</div>
          ) : (
            searchResults.map(opt => (
              <button
                key={`${opt.type}-${opt.id}`}
                onClick={() => {
                  onSelect(opt);
                  setQuery('');
                  setShowResults(false);
                }}
                className="w-full text-left px-3 py-2 hover:bg-cm-bg-hover flex items-center gap-2"
              >
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 ${TARGET_TYPE_COLORS[opt.type]}`}>
                  {opt.type}
                </span>
                <span className="text-sm text-cm-text-primary truncate">{opt.title}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ValueTargetSearch;
